import { useCallback, useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './MainPage.module.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { HeaderType } from '../../components/Header/types/Header';
import { FooterType } from '../../components/Footer/types/Footer';
import Filter from '../../components/FiltersComponents/SmallFiltersComponents/Filter/Filter';
import Filters, { FilterBigState } from '../../components/FiltersComponents/BigFiltersComponents/Filters/Filters';
import Card from '../../components/CardsComponents/FlatCard/FlatCard';
import PromoBlocks from '../../components/PromoBlocks/PromoBlocks';
import Button from '../../components/UI/Button/Button';
import { FLATS_PAGE, NEWS_PAGE } from '../../router/paths';
import NotOnlyFlats from '../../components/NotOnlyFlats/NotOnlyFlats';
import { FilterSmallState } from '../../components/FiltersComponents/SmallFiltersComponents/Filter/Filter';
import StatusProject from '../../components/StatusProject/StatusProject';
import NewsCard, { NewsData } from '../../components/NewsCard/NewsCard';
import { Helmet } from 'react-helmet';
import ActiveFilterButton from '../../components/UI/ActiveFilterButton/ActiveFilterButton';
import SelectButton from '../../components/UI/SelectButton/SelectButton';
import { OptionData } from '../../components/CardsComponents/types';
import NotFoundBlock from '../../components/NotFoundBlock/NotFoundBlock';
import MobileNav from '../../components/MobileNav/MobileNav';
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';
import api from '../../utils/api';
import cn from 'classnames';
import { calculateDiscountedPrice } from '../../utils/discountPriceUtils';


export default function MainPage() {
  const navigate = useNavigate();
  const [cardData, setCardData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [totalFlatsCount, setTotalFlatsCount] = useState<number>(0);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [newsData, setNewsData] = useState<NewsData[]>([]);
  const [displayCount, setDisplayCount] = useState(6);
  const [initialAreaRange, setInitialAreaRange] = useState<[number, number]>([0, 0]);
  const [filters, setFilters] = useState<FilterBigState>({
    type: [],
    window_options: [],
    special_options: [],
    project_options: [],
    price_range: [0, 0],
    floor_range: [0, 0],
    area_range: initialAreaRange,
    projectType: null,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const newsResponse = await api.news.getAll();
        
        if (!Array.isArray(newsResponse)) {
          console.error('[MainPage] API вернул не массив новостей:', newsResponse);
          throw new Error('API вернул неверный формат данных для новостей');
        }
        
        const parseNewsDate = (value: string): number => {
          if (!value) return 0;
          const cleaned = value
            .toString()
            .replace(/\bг\.?|\bгода?\b/gi, '')
            .trim();

          // DD.MM.YYYY
          const dmy = cleaned.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
          if (dmy) {
            const [, d, m, y] = dmy as unknown as [string, string, string, string];
            const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
            return date.getTime();
          }

          // DD <russian_month> YYYY
          const months: Record<string, number> = {
            'января': 0,
            'февраля': 1,
            'марта': 2,
            'апреля': 3,
            'мая': 4,
            'июня': 5,
            'июля': 6,
            'августа': 7,
            'сентября': 8,
            'октября': 9,
            'ноября': 10,
            'декабря': 11,
          };
          const ru = cleaned.toLowerCase().match(/^(\d{1,2})\s+([а-яё]+)\s+(\d{4})$/i);
          if (ru) {
            const [, d, mon, y] = ru as unknown as [string, string, string, string];
            const monthIndex = months[mon];
            if (monthIndex !== undefined) {
              const date = new Date(parseInt(y, 10), monthIndex, parseInt(d, 10));
              return date.getTime();
            }
          }

          const t = Date.parse(cleaned);
          return isNaN(t) ? 0 : t;
        };

        const transformedNews = newsResponse.map((article: any) => ({
          id: article.id,
          project: article.project,
          topic: article.topic,
          date: article.date,
          title: article.title,
          shortDescription: article.shortDescription,
          content: article.content ? article.content.map((block: any) => ({
            type: block.block_type || 'paragraph',
            text: block.text || undefined,
            items: block.items && block.items.length > 0 ? block.items : undefined,
          })) : undefined,
        }));
        const sortedNews = [...transformedNews].sort((a, b) => parseNewsDate(b.date) - parseNewsDate(a.date));
        setNewsData(sortedNews);
        
        const flatsResponse = await api.flats.getAll();
        
        if (!Array.isArray(flatsResponse)) {
          console.error('[MainPage] API вернул не массив квартир:', flatsResponse);
          throw new Error('API вернул неверный формат данных для квартир');
        }
        
        setTotalFlatsCount(flatsResponse.length);
        const availableFlats = flatsResponse.filter((flat: any) => flat.available);
        
        if (availableFlats.length === 0) {
          console.warn('[MainPage] Нет доступных квартир. Все квартиры:', flatsResponse);
        }
        
        availableFlats.sort((a: any, b: any) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));
        setCardData(availableFlats);
        setFilteredData(availableFlats);

        const prices = availableFlats
          .map((flat: any) => {
            const discountedPrice = calculateDiscountedPrice(
              flat.pricePerM2 ?? '0',
              flat.discountProperty ?? flat.discount_property ?? 0,
              flat.price ?? '0'
            );
            return parseInt(discountedPrice.replace(/\s+/g, ''), 10);
          })
          .filter((price: number) => !isNaN(price));

        const floors = availableFlats
          .map((flat: any) => Number(flat.floor))
          .filter((floor: number) => !isNaN(floor));

        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
        const minFloor = floors.length > 0 ? Math.min(...floors) : 0;
        const maxFloor = floors.length > 0 ? Math.max(...floors) : 0;

        setSmallFilters((prevFilters) => ({
          ...prevFilters,
          price_range: [minPrice, maxPrice],
          floor_range: [minFloor, maxFloor],
        }));

        const areas = availableFlats.map((flat: any) => parseFloat((flat.area || '').toString().replace(',', '.'))).filter(a => !isNaN(a));
        const minArea = areas.length > 0 ? Math.min(...areas) : 0;
        const maxArea = areas.length > 0 ? Math.max(...areas) : 0;
        setInitialAreaRange([minArea, maxArea]);
        setFilters(prevFilters => ({
          ...prevFilters,
          price_range: [minPrice, maxPrice],
          floor_range: [minFloor, maxFloor],
          area_range: [minArea, maxArea],
        }));

      } catch (error) {
        console.error('[MainPage] Ошибка при загрузке данных:', error);
        console.error('[MainPage] Детали ошибки:', {
          message: error instanceof Error ? error.message : 'Неизвестная ошибка',
          stack: error instanceof Error ? error.stack : undefined,
          name: error instanceof Error ? error.name : undefined
        });
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchData();
  }, [setIsInitialLoad]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 900) {
        setDisplayCount(3);
      } else {
        setDisplayCount(6);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
  
      if (currentScrollY > lastScrollY) {
        setIsNavVisible(false);
      } else {
        setIsNavVisible(true);
      }
  
      setLastScrollY(currentScrollY);
    };
  
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const [smallFilters, setSmallFilters] = useState<FilterSmallState>({
    type: [],
    price_range: [0, 0],
    floor_range: [0, 0],
  });

  const applySmallFilter = useCallback(
    (filters: FilterSmallState) => {
      setSmallFilters(filters);
      const filtered = cardData.filter((card) => {
        const discountedPrice = calculateDiscountedPrice(
          card.pricePerM2 ?? '0',
          card.discountProperty ?? card.discount_property ?? 0,
          card.price ?? '0'
        );
        const price = parseInt(discountedPrice.replace(/\s+/g, ''), 10);
        const floor = card.floor;

        const matchesType = filters.type.length === 0 || filters.type.includes(card.roomType);
        const matchesPrice = price >= filters.price_range[0] && price <= filters.price_range[1];
        const matchesFloor = floor >= filters.floor_range[0] && floor <= filters.floor_range[1];

        return matchesType && matchesPrice && matchesFloor;
      });

      setFilteredData(filtered);
    },
    [cardData]
  );

  const applyBigFilter = useCallback(
    (newFilters: FilterBigState) => {
      setSmallFilters({
        type: newFilters.type,
        price_range: newFilters.price_range,
        floor_range: newFilters.floor_range,
      });

      const filtered = cardData.filter((card) => {
        const discountedPrice = calculateDiscountedPrice(
          card.pricePerM2 ?? '0',
          card.discountProperty ?? card.discount_property ?? 0,
          card.price ?? '0'
        );
        const price = parseInt(discountedPrice.replace(/\s+/g, ''), 10);
        const floor = card.floor;
        const area = parseFloat((card.area || '').toString().replace(',', '.'));

        const matchesType = newFilters.type.length === 0 || newFilters.type.includes(card.roomType);
        const matchesPrice = price >= newFilters.price_range[0] && price <= newFilters.price_range[1];
        const matchesFloor = floor >= newFilters.floor_range[0] && floor <= newFilters.floor_range[1];
        const matchesArea = newFilters.area_range
          ? area >= newFilters.area_range[0] - 0.05 && area <= newFilters.area_range[1] + 0.05
          : true;
        const matchesWindowOptions =
          newFilters.window_options.length === 0 ||
          newFilters.window_options.every((option) => (card.windowOptions || []).some((wo: OptionData) => wo.name === option));
        const matchesSpecialOptions =
          newFilters.special_options.length === 0 ||
          newFilters.special_options.every((option) => (card.specialOptions || []).some((so: OptionData) => so.name === option));
        const matchesProjectOptions =
          newFilters.project_options.length === 0 ||
          newFilters.project_options.includes(card.project_type);

        return (
          matchesType &&
          matchesPrice &&
          matchesFloor &&
          matchesArea &&
          matchesWindowOptions &&
          matchesSpecialOptions &&
          matchesProjectOptions
        );
      });

      setFilteredData(filtered);
      setFilters(newFilters);
    },
    [cardData]
  );

  const openFiltersModal = () => {
    setIsFiltersModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFiltersModal = () => {
    setIsFiltersModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  const handleRemoveFilter = (filterType: string, value: string | number) => {
    if (filters) {
      const updatedFilters = { ...filters };
      if (filterType === 'window_options') {
        updatedFilters.window_options = updatedFilters.window_options.filter(option => option !== value);
      } else if (filterType === 'special_options') {
        updatedFilters.special_options = updatedFilters.special_options.filter(option => option !== value);
      } else if (filterType === 'area_range') {
        delete updatedFilters.area_range;
      }
      setFilters(updatedFilters);
      applyBigFilter(updatedFilters);
    }
  };

  const resetFilters = () => {
    setFilters({
      area_range: undefined,
      type: [],
      window_options: [],
      special_options: [],
      project_options: [],
      price_range: [0, 0],
      floor_range: [0, 0],
      projectType: null,
    });
    setSmallFilters({
      type: [],
      price_range: [0, 0],
      floor_range: [0, 0],
    });
    setFilteredData(cardData);
  };

  const displayedCards = filteredData.slice(0, displayCount);

  const handleRedirect = () => {
    navigate(FLATS_PAGE);
  };

  const getFlatEnding = (count: number): string => {
    if (count % 10 === 1 && count % 100 !== 11) {
      return 'квартиру';
    } else if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
      return 'квартиры';
    } else {
      return 'квартир';
    }
  };

  const flatsCount = filteredData.length;
  const totalFlatsEnding = getFlatEnding(totalFlatsCount);

  const hasActiveFilters = filters && (
    filters.window_options.length > 0 ||
    filters.special_options.length > 0 ||
    (filters.area_range && (filters.area_range[0] !== initialAreaRange[0] || filters.area_range[1] !== initialAreaRange[1]))
  );

  return (
    <>
      <Helmet>
        <title>Восток Строй | Ведущий девелопер Каспийска</title>
        <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" />
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vostokstroy.com/" />

        <meta property="og:title" content="Ведущему девелоперу Каспийска — 10 лет" />
        <meta property="og:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ведущему девелоперу Каспийска — 10 лет" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />

        <meta name="theme-color" content="#ffffff"/>
        <meta name="mobile-web-app-capable" content="yes"/>
        <meta name="apple-mobile-web-app-status-bar-style" content="default"/>

        <link rel="icon" type="image/svg+xml" href="/favicons/main-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/main-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/main-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/main-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/main-fav-180x180.png" />
      </Helmet>

      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
          {!isFiltersModalOpen && <Header theme={HeaderType.Light} />}
          <div className={styles.content}>
            <div className={styles.content_blocks}>
              <PromoBlocks/>
            </div>
            <div className={styles.flat_wrapper}>
              <div className={styles.flat_content}>
                <div className={styles.page_title}>
                  <h2>Квартиры</h2>
                </div>
                <div className={styles.flat_inner}>
                  <Filter
                    onApplyFilter={applySmallFilter}
                    cardData={cardData}
                    onOpenFiltersModal={openFiltersModal}
                    smallFilters={smallFilters}
                    setSmallFilters={setSmallFilters}
                  />
                  {hasActiveFilters && (
                    <div className={styles.active_filters}>
                      {filters.window_options.map(option => (
                        <ActiveFilterButton
                          key={option}
                          label={option}
                          onClick={() => handleRemoveFilter('window_options', option)}
                          theme='light'
                        />
                      ))}
                      {filters.special_options.map(option => (
                        <ActiveFilterButton
                          key={option}
                          label={option}
                          onClick={() => handleRemoveFilter('special_options', option)}
                          theme='light'
                        />
                      ))}
                      {filters?.area_range && (filters.area_range[0] !== initialAreaRange[0] || filters.area_range[1] !== initialAreaRange[1]) && (
                        <ActiveFilterButton
                          label={`${filters.area_range[0].toLocaleString('ru-RU')}-${filters.area_range[1].toLocaleString('ru-RU')} м²`}
                          onClick={() => handleRemoveFilter('area_range', '')}
                          theme='light'
                        />
                      )}
                      {hasActiveFilters && (
                        <SelectButton onClick={resetFilters} theme='light'>
                          Сбросить фильтры
                        </SelectButton>
                      )}
                    </div>
                  )}
                  <div className={styles.list_wrapper}>
                    {filteredData.length === 0 ? (
                      <NotFoundBlock type="filter" />
                    ) : (
                      <div className={styles.flat_list}>
                        {displayedCards.map((data) => (
                          <Card key={data.id} data={data} isDiscounted={!!data.discountProperty} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <Button
                  width='100%'
                  colorType='gray'
                  roundType='small_rounded'
                  onClick={handleRedirect}
              >
                  {flatsCount < 6 ? 'Открыть все квартиры' : `Открыть ${totalFlatsCount} ${totalFlatsEnding}`}
              </Button>
            </div>
            <NotOnlyFlats />
            <div className={styles.news_wrapper}>
              <h2>Новости</h2>
              <div className={styles.news_cards}>
                {newsData.slice(0, 3).map((data) => (
                  <NewsCard
                    key={data.id}
                    id={data.id}
                    project={data.project}
                    topic={data.topic}
                    date={data.date}
                    title={data.title}
                    shortDescription={data.shortDescription}
                    content={data.content}
                  />
                ))}
              </div>
              <Link to={NEWS_PAGE} className={styles.news_button}>
                <Button width='100%' colorType='gray' roundType='small_rounded'>
                  Смотреть все
                </Button>
              </Link>
            </div>
          </div>
          <StatusProject />

          <Footer theme={FooterType.Light} />
          {isMobile && (
            <div className={cn(styles.mobile_nav_container, isNavVisible ? styles.show : styles.hide)}>
              <MobileNav />
            </div>
          )}

          {isFiltersModalOpen && (
            <Filters
              onApplyFilter={applyBigFilter}
              onClose={closeFiltersModal}
              cardData={cardData}
              smallFilters={smallFilters}
              setSmallFilters={setSmallFilters}
            />
          )}
        </>
      )}
    </>
  );
}
